import { Component, IComponentOptions } from '../component';
import { GenericComponentFactory, IComponentFactoryOptions } from '../component-factory';
import * as dom from '../dom';
import { createKeypressEventListener, IKeyPressHandlers } from '../keypress-handler';
import { RegionSettings } from '../regional';
import * as utility from '../utility';
import { ListItem } from './list-item';
import { LiveRegion } from './live-region';

// TODO: ENHANCEMENT: Add the ability to put values into hidden inputs for form submittion

export interface IListComponentOptions extends IComponentOptions {
	componentClass?: string;
	listItemClass?: string;
}
export type VariantType = 'static' | 'actionable' | 'checkable';

const VARIANTS = {
	static: 'static',
	actionable: 'actionable',
	checkable: 'checkable',
};

export class ListComponent extends Component {

	private _listItems: ListItem[] = [];
	private _statusRegion: LiveRegion = LiveRegion.globalStatusRegion;

	private _mutationObserver: MutationObserver = new MutationObserver((info) => {
		this._updateListRole();
		this._updateListItems();
	});

	constructor(element: HTMLElement, options: IListComponentOptions = {}) {
		super(element, options);

		this._updateListItems();
		this._updateListRole();

		this.element.addEventListener('_listitemselected', this._listItemSelectedHandler);
		this.element.addEventListener('keydown', this._keypressHandler);
		this.element.addEventListener('blur', this._blurHandler);
		this.element.addEventListener('focus', this._focusHandler);
		this._mutationObserver.observe(this.element, { attributes: true, childList: true, subtree: true });
	}

	get activeListItem(): ListItem {
		const activeDescendantId = this.element.getAttribute('aria-activedescendant');
		return this.listItems.find((listItem) => listItem.id === activeDescendantId) || null;
	}

	set activeListItem(listItem: ListItem) {
		const id = listItem && listItem.id || null;
		const activeDescendantId = this.element.getAttribute('aria-activedescendant');

		if (activeDescendantId === id) {
			return;
		}

		this.listItems.forEach((li) => {
			li.active = id === li.id;
		});

		if (id) {
			this.element.setAttribute('aria-activedescendant', id);
		} else {
			this.element.removeAttribute('aria-activedescendant');
		}

		if (!listItem) {
			return;
		}

		dom.scrollIntoViewIfNeeded(listItem.element, true);
	}

	get activeListItemIndex(): number {
		const activeDescendantId = this.element.getAttribute('aria-activedescendant');
		return this.listItems.findIndex((listItem) => listItem.id === activeDescendantId);
	}

	get listItems(): ListItem[] {
		return this._listItems;
	}

	get multiselect(): boolean {
		return this.element.getAttribute('aria-multiselectable') === 'true';
	}

	set multiselect(bool: boolean) {
		this.element.setAttribute('aria-multiselectable', bool.toString());
	}

	get readOnly(): boolean {
		const variant = this.variant;

		return variant === VARIANTS.static;
	}

	set selectedListItems(listItems: ListItem[]) {
		const oldSelectedListItems = this.selectedListItems;

		const selectedListItems = this.listItems.filter((listItem) => {
			return listItems.find((selectedListItem) => selectedListItem.id === listItem.id && !listItem.disabled);
		});

		const sameListItems = selectedListItems.filter((selectedListItem) => {
			return oldSelectedListItems.find((oldListItem) => oldListItem.id === selectedListItem.id);
		});

		// if the items has changed we will update them and fire an event.
		if (selectedListItems.length !== oldSelectedListItems.length || selectedListItems.length !== sameListItems.length) {

			this.listItems.forEach((listItem) => {
				listItem.selected = !!selectedListItems.find((selectedListItem) => selectedListItem.id === listItem.id);
			});
			this._dispatchChangeEvent();
		}
	}

	get selectedListItems(): ListItem[] {
		return this.listItems.filter((item) => item.selected);
	}

	get value(): string | string[] {
		const selectedItems = this.selectedListItems || [];
		if (this.multiselect) {
			return selectedItems.map((listItem) => listItem.value);
		}

		return (selectedItems[0] && selectedItems[0].value) || null;
	}

	set value(value: string | string[]) {
		const values = typeof value === 'string' ? [value] : value;
		const selectedListItems = this.listItems.filter((listItem) => values.find((val) => val === listItem.value)) || [];

		this.selectedListItems = selectedListItems;
	}

	get variant(): VariantType {
		const variants = Object.values(VARIANTS) as VariantType[];
		const variant = (utility.splitAttributeToList(this.element.getAttribute('class')).find((className) => {
			return !!variants.find((_variant) => this._options.componentClass + '--' + _variant === className);
		}) || this._options.componentClass + '--' + VARIANTS.static).replace(this._options.componentClass + '--', '') as VariantType;

		return variant;
	}

	set variant(variant: VariantType) {
		this.element.classList.add(this._options.componentClass + '--' + variant);
		this._updateListRole();
	}

	public destroy(): void {
		this._mutationObserver.disconnect();

		if (this.element) {
			this.element.removeEventListener('_listitemselected', this._listItemSelectedHandler);
			this.element.removeEventListener('keydown', this._keypressHandler);
			this.element.removeEventListener('blur', this._blurHandler);
			this.element.removeEventListener('focus', this._focusHandler);
		}

		this.listItems.forEach((listItem) => {
			listItem.destroy();
		});
	}

	private get _options(): IListComponentOptions {
		return this.getOptions() as IListComponentOptions;
	}

	private _listItemSelectedHandler = (event: CustomEvent) => {
		this._selectListItem(event.detail.component as ListItem);
	}

	private _keypressHandler = (event: Event) => {
		return createKeypressEventListener({
			up: this._decreaseActiveItemPosition,
			down: this._increaseActiveItemPosition,
			left: this._decreaseActiveItemPosition,
			right: this._increaseActiveItemPosition,
			spacebar: this._selectActiveItem,
			enter: this._selectActiveItem,
			escape: () => {
				this.activeListItem = null;
				return false;
			},
			tab: () => {
				return true;
			},
		} as IKeyPressHandlers)(event);
	}

	private _blurHandler = (event: Event) => {
		this.activeListItem = null;
	}

	private _focusHandler = (event: Event) => {
		this.activeListItem = this.selectedListItems[0] || null;
	}

	private _dispatchChangeEvent() {
		dom.dispatchEvent(this.element, 'change', {
			component: this,
			value: this.value,
		});
	}

	private _decreaseActiveItemPosition = (): boolean => {
		const activeListItem = this.listItems[this._moveActiveItemPosition(-1)];
		this.activeListItem = activeListItem;

		this._statusRegion.broadcast(RegionSettings.format(activeListItem.disabled ? 'list.sr.disabled' : 'list.sr.active', activeListItem.text));
		return false;
	}

	private _increaseActiveItemPosition = (): boolean => {
		const activeListItem = this.listItems[this._moveActiveItemPosition(1)];
		this.activeListItem = activeListItem;

		this._statusRegion.broadcast(RegionSettings.format(activeListItem.disabled ? 'list.sr.disabled' : 'list.sr.active', activeListItem.text));
		return false;
	}

	private _moveActiveItemPosition = (steps: number): number => {
		const activeIndex = this.activeListItemIndex;
		const listItems = this.listItems;
		const minIndex = 0;
		const maxIndex = listItems.length - 1;
		let movedIndex = activeIndex + steps;

		if (movedIndex < minIndex) {
			movedIndex = 0;
		}

		if (movedIndex > maxIndex) {
			movedIndex = maxIndex;
		}

		return movedIndex;
	}

	private _selectActiveItem = (): boolean => {
		const activeListItem = this.activeListItem;
		if (!activeListItem || activeListItem.disabled) {
			return false;
		}

		this._selectListItem(activeListItem);
		this._statusRegion.broadcast(RegionSettings.format(activeListItem.selected ? 'list.sr.selected' : 'list.sr.unselected', activeListItem.text));

		return false;
	}

	private _selectListItem(listItem: ListItem) {
		let selectedListItems = this.selectedListItems;

		if (this.multiselect) {
			const listItemIndex = selectedListItems.findIndex((selectedListItem) => selectedListItem.id === listItem.id);
			if (listItemIndex >= 0) {
				selectedListItems = selectedListItems.filter((selectedListItem) => selectedListItem.id !== listItem.id);
			} else {
				selectedListItems.push(listItem);
			}
		} else {
			selectedListItems = [listItem];
		}

		this.selectedListItems = selectedListItems;
	}

	private _updateListRole(): void {
		const previousRole = this.element.getAttribute('role');
		const previousTabindex = this.element.getAttribute('tabindex') || null;

		const role = this.readOnly ? 'list' : 'listbox';
		const tabindex = this.readOnly ? null : '0';

		if (previousRole !== role) {
			this.element.setAttribute('role', role);
		}

		if (previousTabindex !== tabindex) {
			if (tabindex) {
				this.element.setAttribute('tabindex', tabindex);
			} else {
				this.element.removeAttribute('tabindex');
			}
		}
	}

	private _updateListItems(): void {
		this._listItems = utility.toArray(this.element.querySelectorAll('.' + this._options.listItemClass))
			.map((listItemElement: HTMLElement) => {
				const activeListItemId = this.element.getAttribute('aria-activedescendant') || null;
				const listItem = this._listItems.find((oldItem) => oldItem.id === listItemElement.id) || new ListItem(listItemElement);
				listItem.readOnly = this.readOnly;

				if (listItem.active && listItem.id !== activeListItemId) {
					this.activeListItem = listItem;
				}

				return listItem;
			});
	}

	private static _defaultComponentOptions: IListComponentOptions = {
		componentClass: 'iris-list',
		listItemClass: 'iris-list-item',
		idPrefix: 'iris_list',
	};

	private static _factoryOptions: IComponentFactoryOptions = {
		defaultQuerySelector: '.' + ListComponent._defaultComponentOptions.componentClass,
		componentName: 'ListComponent'
	};

	public static factory = new GenericComponentFactory<ListComponent, IListComponentOptions>(ListComponent, ListComponent._factoryOptions, ListComponent._defaultComponentOptions);

	public static VARIANTS = VARIANTS;
}
